import { DEFAULT_USER_SETTINGS } from "@taskfortress/core"
import React, { useCallback, useMemo, useState } from "react"
import { useData } from "../../providers/DataProvider"
import { DatePickerCore } from "../DatePicker"
import { FilterModal } from "../FilterModal"
import { Box } from "../gluestack/box"
import { TagChip } from "../tags/TagChip"
import { TagsPanel } from "../tags/TagsPanel"
import { TaskList } from "./TaskList"

interface TasksDesktopProps {
  currentDate: string
  onCurrentDateChange: (date: string) => void
}

export const TasksDesktop: React.FC<TasksDesktopProps> = ({
  currentDate,
  onCurrentDateChange,
}) => {
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false)
  const { setSelectedTasksTag, selectedTasksTag, tags, userSettings, tasks } =
    useData()

  // Get the selected tag for display
  const selectedTag = useMemo(() => {
    if (!selectedTasksTag) return null
    return tags.find((tag) => tag.id === selectedTasksTag)
  }, [selectedTasksTag, tags])

  const handleSidebarDateSelect = useCallback(
    (date: string) => {
      onCurrentDateChange(date)
    },
    [onCurrentDateChange],
  )

  const handleTagSelect = useCallback(
    (tagId: string | null) => {
      setSelectedTasksTag(tagId)
    },
    [setSelectedTasksTag],
  )

  return (
    <Box className="flex-1 flex-row">
      {/* Left sidebar with calendar and tags */}
      <Box className="w-64 flex-none flex-col border-r border-border bg-sidebar-400 shadow-sm">
        {/* Calendar */}
        <Box className="border-b border-border">
          <DatePickerCore
            selectedDate={currentDate}
            onDateSelect={handleSidebarDateSelect}
            showTodayButton={true}
            showTaskIndicators={true}
            tasks={tasks}
            firstDayOfWeek={
              userSettings.firstDayOfWeek ||
              DEFAULT_USER_SETTINGS.firstDayOfWeek!
            }
            className="shadow-none border-none bg-transparent"
            headerContainerClassName="border-none py-0 px-0"
            todayButtonContainerClassName="border-none py-0 px-0"
          />
        </Box>

        <TagsPanel
          className="flex-1 bg-sidebar-100"
          onTagSelect={handleTagSelect}
          selectedTagId={selectedTag?.id}
        />
      </Box>

      {/* Main content area */}
      <Box className="flex-1 flex-col">
        <Box className="flex-row justify-center items-center px-4 py-3 border-b border-border bg-outline-300">
          <Box className="flex-row items-center gap-2">
            {selectedTag ? (
              <>
                <Box className="text-sm font-medium">Filtered by tag:</Box>
                <TagChip
                  tagName={selectedTag.name}
                  className="px-3 py-1"
                  textClassName="text-xs font-medium"
                />
              </>
            ) : (
              <Box className="text-sm font-medium">Showing all items</Box>
            )}
          </Box>
        </Box>
        <TaskList
          currentDate={currentDate}
          onCurrentDateChange={onCurrentDateChange}
          selectedTag={selectedTag}
        />
      </Box>

      {/* Modals */}
      <FilterModal
        isVisible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
      />
    </Box>
  )
}
